import { CSSProperties, useContext, useEffect, useMemo } from "react"
import { Grid, Stack, useMediaQuery, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"
import PageContainer from "../../../components/global/PageContainer"
import FadeFromTop from "../../../components/animations/FadeFromTop"
import Card from "../../../components/global/Card"
import Title from "../../../components/global/Title"
import Text from "../../../components/global/Text"
import BarChartLegendItem from "../../../components/global/BarChartLegendItem"
import MobilityLineChart, {
  MobilityLineChartDataset,
} from "../../../components/mobility/MobilityLineChart"
import MobilityDonutCard from "../../../components/mobility/MobilityDonutCard"
import { AuthContext } from "../../../controllers/auth"
import { MobilityContext } from "../../../controllers/mobility"
import { colors } from "../../../services/config/colors"
import {
  formatMobilityNumber,
  getMonthLabel,
  getTransportModeColor,
  getTransportModeLabel,
  groupTopItems,
  MobilityChartItem,
} from "../../../services/utils/mobility"
import {
  CommuteEmissionsMonthItem,
  CommuteModeMonthItem,
} from "../../../models/mobility.models"

// modes of transport of the commute emissions data, in the order of the Looker report's
// area chart (series) and of its scorecards
const sustainableModes = [
  "train",
  "walk",
  "subway",
  "bus",
  "bicycle",
  "tram",
] as const
const sustainableModeTiles = [
  "walk",
  "train",
  "subway",
  "tram",
  "bus",
  "bicycle",
] as const
const unsustainableModes = ["car"] as const

// average km per user-month over some rows: sum(km) / sum(entries), as Looker's AVG over the unpivoted rows
const averageKm = (rows: CommuteModeMonthItem[]) => {
  const entries = rows.reduce((sum, row) => sum + (row.entries ?? 0), 0)
  const km = rows.reduce((sum, row) => sum + (row.distanceKm ?? 0), 0)

  return entries ? km / entries : 0
}

const MobilityManagement = () => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const matchesLG = useMediaQuery(theme.breakpoints.up("lg"))
  const { team } = useContext(AuthContext)
  const {
    commuteEmissionsDataLoading,
    commuteEmissionsMonthlyList,
    commuteModesMonthlyList,
    getCommuteEmissionsData,
  } = useContext(MobilityContext)

  // get data
  useEffect(() => {
    if (team) {
      getCommuteEmissionsData()
    }
  }, [team])

  const loading = commuteEmissionsDataLoading
  const noData = !loading && !commuteEmissionsMonthlyList.length

  // months of the report and their labels
  const months = useMemo(
    () => commuteEmissionsMonthlyList.map((item) => item.month),
    [commuteEmissionsMonthlyList]
  )
  const monthLabels = useMemo(
    () => months.map((month) => getMonthLabel(t, month)),
    [months, i18n.language]
  )

  // sum of a column over all months
  const sumOf = (field: keyof CommuteEmissionsMonthItem) =>
    commuteEmissionsMonthlyList.reduce(
      (sum, item) => sum + ((item[field] as number | null) ?? 0),
      0
    )

  // mode rows of a month, optionally of some modes or of a sustainability
  const modeRows = (filter: (row: CommuteModeMonthItem) => boolean) =>
    commuteModesMonthlyList.filter(filter)
  const monthlySeries = (
    filter: (row: CommuteModeMonthItem) => boolean,
    value: (rows: CommuteModeMonthItem[]) => number
  ) =>
    months.map((month) =>
      value(modeRows((row) => row.month === month && filter(row)))
    )

  // kpis
  const totalCo2Kg = useMemo(() => sumOf("co2Kg"), [commuteEmissionsMonthlyList])
  const meanCo2KgPerCommute = useMemo(() => {
    const trips = sumOf("trips")

    return trips ? sumOf("co2FromMeanKg") / trips : 0
  }, [commuteEmissionsMonthlyList])
  const usersAnalyzed = commuteEmissionsMonthlyList[0]?.teamUsers ?? 0

  // average km per month, sustainable vs unsustainable
  const sustainabilitySeries = useMemo(
    () =>
      (["sustainable", "unsustainable"] as const).map((sustainability) => ({
        sustainability,
        data: monthlySeries(
          (row) => row.sustainability === sustainability,
          averageKm
        ),
      })),
    [commuteModesMonthlyList, months]
  )
  const sustainabilityDatasets: MobilityLineChartDataset[] =
    sustainabilitySeries.map((item) => ({
      label: t(item.sustainability),
      data: item.data,
      color: colors.mobility[item.sustainability],
    }))
  // the same averages as a share of each month
  const sustainabilityShareDatasets: MobilityLineChartDataset[] =
    sustainabilitySeries.map((item) => ({
      label: t(item.sustainability),
      data: item.data.map((value, index) => {
        const total = sustainabilitySeries.reduce(
          (sum, series) => sum + series.data[index],
          0
        )

        return total ? (value / total) * 100 : 0
      }),
      color: colors.mobility[item.sustainability],
    }))

  // average km per month by sustainable mode, total km per month by unsustainable mode
  const sustainableModeDatasets: MobilityLineChartDataset[] = useMemo(
    () =>
      sustainableModes.map((mode, index) => ({
        label: getTransportModeLabel(t, mode),
        data: monthlySeries((row) => row.mode === mode, averageKm),
        color: getTransportModeColor(mode, index),
      })),
    [commuteModesMonthlyList, months, i18n.language]
  )
  const unsustainableModeDatasets: MobilityLineChartDataset[] = useMemo(
    () =>
      unsustainableModes.map((mode, index) => ({
        label: getTransportModeLabel(t, mode),
        data: monthlySeries(
          (row) => row.mode === mode,
          (rows) => rows.reduce((sum, row) => sum + (row.distanceKm ?? 0), 0)
        ),
        color: getTransportModeColor(mode, index),
      })),
    [commuteModesMonthlyList, months, i18n.language]
  )

  // average monthly commute length of each mode over all months, and per month
  const modeAverage = (mode: string) =>
    averageKm(modeRows((row) => row.mode === mode))
  const modeMonthlyAverage = (mode: string) =>
    monthlySeries((row) => row.mode === mode, averageKm)

  // donuts: km per mode and how many user-months used each mode (top 7 + others, 8 slices as in Looker)
  const getModeItems = (
    value: (rows: CommuteModeMonthItem[]) => number
  ): MobilityChartItem[] =>
    groupTopItems(
      [...sustainableModes, ...unsustainableModes].map((mode, index) => ({
        id: mode,
        label: getTransportModeLabel(t, mode),
        value: value(modeRows((row) => row.mode === mode)),
        color: getTransportModeColor(mode, index),
      })),
      7,
      t("others")
    )
  const distanceItems = useMemo(
    () =>
      getModeItems((rows) =>
        rows.reduce((sum, row) => sum + (row.distanceKm ?? 0), 0)
      ),
    [commuteModesMonthlyList, i18n.language]
  )
  const registeredItems = useMemo(
    () =>
      getModeItems((rows) =>
        rows.reduce((sum, row) => sum + (row.usedEntries ?? 0), 0)
      ),
    [commuteModesMonthlyList, i18n.language]
  )

  const kpiWidth = matchesLG ? "calc(100% / 3 - 16px)" : "100%"

  const noDataText = (
    <Text fontWeight={500} color={colors.textSecondary}>
      {t("no_data")}
    </Text>
  )

  // card with a monthly line or area chart and its legend
  const chartCard = (
    title: string,
    description: string | undefined,
    datasets: MobilityLineChartDataset[],
    options: {
      variant?: "line" | "area" | "stacked"
      yTitle?: string
      y1Title?: string
      yMax?: number
    } = {}
  ) => (
    <Card style={{ overflowX: "scroll" }}>
      <Stack gap={3} style={{ minWidth: 880 }}>
        <Stack gap={1}>
          <Title>{title}</Title>
          {description ? (
            <Text fontSize={14} fontWeight={500} color={colors.textSecondary}>
              {description}
            </Text>
          ) : null}
        </Stack>
        {noData ? (
          noDataText
        ) : (
          <>
            <div style={{ width: "100%", height: 280, position: "relative" }}>
              <MobilityLineChart
                labels={monthLabels}
                datasets={datasets}
                variant={options.variant}
                yTitle={options.yTitle}
                y1Title={options.y1Title}
                yMax={options.yMax}
                loading={loading}
              />
            </div>
            <Stack
              direction="row"
              gap={2}
              justifyContent="flex-end"
              flexWrap="wrap"
            >
              {datasets.map((item) => (
                <BarChartLegendItem
                  key={item.label}
                  label={item.label}
                  color={item.color}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  )

  // scorecard with a monthly line sparkline, as Looker's scorecards: title, optional
  // description, metric label, value with its unit and the sparkline
  const kpiCard = (item: {
    title: string
    description?: string
    label?: string
    value: number
    unit?: string
    decimalPlaces?: number
    series: number[]
    color: string
    width?: string
    sparklineHeight?: number
    style?: CSSProperties
  }) => (
    <Card
      key={item.title}
      gap={2}
      width={item.width}
      style={{ height: "100%", ...item.style }}
    >
      <Stack gap={1}>
        <Title>{item.title}</Title>
        {item.description ? (
          <Text fontSize={14} fontWeight={500} color={colors.textSecondary}>
            {item.description}
          </Text>
        ) : null}
      </Stack>
      {/* value and sparkline sit at the bottom, so they line up across cards of equal height */}
      <Stack gap={0.5} style={{ marginTop: "auto" }}>
        {item.label ? (
          <Text fontSize={13} fontWeight={500} color={colors.textSecondary}>
            {item.label}
          </Text>
        ) : null}
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Title
            fontSize={24}
            lineHeight="28px"
            color={colors.primary}
            loading={loading}
          >
            {noData
              ? t("no_data")
              : formatMobilityNumber(item.value, item.decimalPlaces ?? 1)}
          </Title>
          {!loading && !noData && item.unit ? (
            <Text fontSize={13}>{item.unit}</Text>
          ) : null}
        </Stack>
      </Stack>
      {noData ? null : (
        <div
          style={{
            width: "100%",
            height: item.sparklineHeight ?? 120,
            position: "relative",
          }}
        >
          <MobilityLineChart
            labels={monthLabels}
            datasets={[
              {
                label: item.label ?? item.title,
                data: item.series,
                color: item.color,
              },
            ]}
            compact
            loading={loading}
          />
        </div>
      )}
    </Card>
  )

  // scorecards with the average monthly commute length of some modes and its sparkline
  const modeTiles = (modes: readonly string[]) => (
    <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 3, xl: 6 }}>
      {modes.map((mode, index) => (
        <Grid key={mode} item xs={1}>
          {kpiCard({
            title: getTransportModeLabel(t, mode),
            value: modeAverage(mode),
            unit: "km",
            series: modeMonthlyAverage(mode),
            color: getTransportModeColor(mode, index),
            sparklineHeight: 64,
            // tiles inside the section card
            style: { padding: 16, border: `1px solid ${colors.strokeLight}` },
          })}
        </Grid>
      ))}
    </Grid>
  )

  return (
    <PageContainer
      title={t("mobility_management")}
      breadcrumbs={[{ title: t("mobility_management") }]}
      withFilters={false}
      withDatePicker={false}
      infoText={t("mobility_management_info")}
    >
      {/* carbonic intensity and co2 emitted per month, two lines on two axes as in Looker */}
      <FadeFromTop>
        {chartCard(
          t("commute_co2e_and_carbonic_intensity"),
          t("commute_carbonic_intensity_description"),
          [
            {
              label: t("carbonic_intensity"),
              data: commuteEmissionsMonthlyList.map(
                (item) => item.carbonicIntensity ?? 0
              ),
              color: colors.mobility.intensity,
              yAxisID: "y1",
            },
            {
              label: t("co2e_emitted"),
              data: commuteEmissionsMonthlyList.map((item) => item.co2Kg ?? 0),
              color: colors.mobility.co2,
            },
          ],
          { yTitle: t("kg_co2e"), y1Title: t("kg_co2e_per_km") }
        )}
      </FadeFromTop>
      {/* scorecards */}
      <FadeFromTop delay={0.05} style={{ marginTop: 24 }}>
        <Stack
          direction={{ md: "column", lg: "row" }}
          gap={3}
          alignItems="stretch"
        >
          {kpiCard({
            title: t("total_co2e_produced"),
            description: t("commute_total_co2e_description"),
            label: t("co2_eq"),
            value: totalCo2Kg,
            unit: "kg",
            series: commuteEmissionsMonthlyList.map((item) => item.co2Kg ?? 0),
            color: colors.mobility.co2,
            width: kpiWidth,
            // stretch to the tallest card in the row (height: 100% would stop that)
            style: { height: "auto" },
          })}
          {kpiCard({
            title: t("mean_co2e_per_commute"),
            description: t("commute_mean_co2e_description"),
            label: t("average_co2_eq"),
            value: meanCo2KgPerCommute,
            unit: "kg",
            decimalPlaces: 2,
            series: commuteEmissionsMonthlyList.map((item) =>
              item.trips ? (item.co2FromMeanKg ?? 0) / item.trips : 0
            ),
            color: colors.mobility.co2,
            width: kpiWidth,
            style: { height: "auto" },
          })}
          {kpiCard({
            title: t("users_analyzed"),
            description: t("commute_users_analyzed_description"),
            label: t("number_of_users"),
            value: usersAnalyzed,
            decimalPlaces: 0,
            series: commuteEmissionsMonthlyList.map((item) => item.users ?? 0),
            color: colors.mobility.co2,
            width: kpiWidth,
            style: { height: "auto" },
          })}
        </Stack>
      </FadeFromTop>
      {/* average km per month, sustainable vs unsustainable: stacked and 100% stacked areas */}
      <FadeFromTop delay={0.1} style={{ marginTop: 24 }}>
        {chartCard(
          t("sustainable_unsustainable_km_per_month"),
          t("sustainable_unsustainable_km_per_month_description"),
          sustainabilityDatasets,
          { variant: "stacked", yTitle: t("kilometers") }
        )}
      </FadeFromTop>
      <FadeFromTop delay={0.1} style={{ marginTop: 24 }}>
        {chartCard(
          t("sustainable_unsustainable_km_share_per_month"),
          t("sustainable_unsustainable_km_share_per_month_description"),
          sustainabilityShareDatasets,
          { variant: "stacked", yTitle: t("km_share_percent"), yMax: 100 }
        )}
      </FadeFromTop>
      {/* km per mode and most registered modes */}
      <FadeFromTop delay={0.15} style={{ marginTop: 24 }}>
        <Stack direction={{ md: "column", lg: "row" }} gap={3}>
          <MobilityDonutCard
            title={t("distance_done_per_transport")}
            items={distanceItems}
            unit="km"
            loading={loading}
            width={matchesLG ? "calc(50% - 12px)" : "100%"}
          />
          <MobilityDonutCard
            title={t("most_registered_transport")}
            items={registeredItems}
            unit={t("registrations")}
            loading={loading}
            width={matchesLG ? "calc(50% - 12px)" : "100%"}
          />
        </Stack>
      </FadeFromTop>
      {/* sustainable means of transport: stacked areas as in Looker */}
      <FadeFromTop delay={0.2} style={{ marginTop: 24 }}>
        {chartCard(
          t("average_km_per_month_sustainable_transport"),
          undefined,
          sustainableModeDatasets,
          { variant: "stacked", yTitle: t("kilometers") }
        )}
      </FadeFromTop>
      <FadeFromTop delay={0.2} style={{ marginTop: 24 }}>
        <Card gap={2}>
          <Title>{t("average_monthly_commute_length_sustainable")}</Title>
          {modeTiles(sustainableModeTiles)}
        </Card>
      </FadeFromTop>
      {/* unsustainable means of transport */}
      <FadeFromTop delay={0.25} style={{ marginTop: 24 }}>
        {chartCard(
          t("km_per_month_unsustainable_transport"),
          undefined,
          unsustainableModeDatasets,
          { variant: "area", yTitle: t("kilometers") }
        )}
      </FadeFromTop>
      <FadeFromTop delay={0.25} style={{ marginTop: 24 }}>
        <Card gap={2}>
          <Title>{t("average_monthly_commute_length_unsustainable")}</Title>
          {modeTiles(unsustainableModes)}
        </Card>
      </FadeFromTop>
    </PageContainer>
  )
}

export default MobilityManagement
